import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Routes } from 'config';
import { useCourseContext } from 'context';

import { Icon } from '../icons';
import { Button } from '../button';

import { StepButton } from './step-button';
import { BurgerButton } from './burger-button';
import { NavigationLink } from './navigation-link';
import { LanguageButton } from './language-button';

const links = [
  {
    i18nKey: 'dashboard',
    href: Routes.Dashboard.Home,
  },
  {
    i18nKey: 'resources',
    href: Routes.Resources.Home,
  },
  {
    i18nKey: 'contactUs',
    href: Routes.ContactUs.Home,
  },
];

const commonButtonStyle =
  'flex min-w-10 items-center justify-center rounded-full h-10 w-10';

export const TopNavigation = () => {
  const { steps } = useCourseContext();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useTranslation(['header']);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="relative">
      <div className="flex w-full flex-col gap-4 bg-white py-3 md:flex-row md:items-center md:justify-between md:gap-x-2">
        <div className="flex items-center">
          <Link
            to={Routes.Dashboard.Home}
            className="flex gap-2 md:gap-3 [&_img]:h-6 md:[&_img]:h-10"
          >
            <img
              src="/logo.svg"
              className="w-auto"
              alt="Transforming Thyroid Treatment Together Logo"
            />
            <img alt="CHOiCE Logo" className="w-auto" src="/CHOiCE-logo.png" />
          </Link>
        </div>
        <div className="absolute left-1/2 hidden flex-1 -translate-x-1/2 pl-8 xl:flex">
          {links.map((link) => (
            <NavigationLink
              key={link.href}
              href={link.href}
              variant="desktop"
              label={t(link.i18nKey)}
              isActive={pathname === link.href}
            />
          ))}
        </div>

        <div className="flex items-center">
          <div className="flex w-full">
            {steps?.map((item, index) => (
              <StepButton
                item={item}
                key={item.id}
                showDivider={index !== 0}
                onClick={() => {
                  const { id, resourceVersions } = item;
                  const isResource =
                    resourceVersions && resourceVersions.length > 0;
                  if (isResource) {
                    navigate(Routes.Course.Resource.replace(':id', id));
                  } else {
                    navigate(Routes.Course.Form.replace(':id', id));
                  }
                }}
              />
            ))}
          </div>
          <LanguageButton className="ml-6 hidden xl:flex" />

          <Button
            component={Link}
            color="secondary"
            to={Routes.Profile.Home}
            className={twMerge(commonButtonStyle, 'ml-8 hidden xl:flex')}
          >
            <Icon.User />
          </Button>

          <BurgerButton
            isOpen={isMobileMenuOpen}
            onClick={handleMobileMenuToggle}
            className="absolute top-3 right-0 ml-4 md:static xl:hidden"
          />
        </div>
        {isMobileMenuOpen && (
          <div className="fixed top-21 right-0 bottom-0 left-0 z-50 mt-px md:top-16 xl:hidden">
            <div
              onClick={handleMobileMenuClose}
              className="bg-black-primary/90 absolute inset-0"
            />
            <div className="bg-white-primary divide-blue-secondary-primary relative max-h-full divide-y-[1.5px] overflow-y-auto">
              {links.map((item) => (
                <NavigationLink
                  key={item.href}
                  href={item.href}
                  variant="mobile"
                  label={t(item.i18nKey)}
                  onClick={handleMobileMenuClose}
                  isActive={pathname === item.href}
                />
              ))}

              <NavigationLink
                variant="mobile"
                className="h-17"
                label={t('account')}
                href={Routes.Profile.Home}
                onClick={handleMobileMenuClose}
                isActive={pathname === Routes.Profile.Home}
                icon={
                  <div className="bg-blue-secondary-primary hover:bg-blue-secondary-hover flex h-10 w-10 items-center justify-center rounded-full">
                    <Icon.User />
                  </div>
                }
              />
              <LanguageButton className="mt-4" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
